import CouponCodeApiService from '@package/core/src/couponCode/CouponCodeApiService';
import CouponCodeInteractor from '@package/core/src/couponCode/CouponCodeInteractor';
import { CouponCodeRequest } from '@package/core/src/couponCode/types';
import { ApiError } from '@package/services/src/ApiClient';

import { apiClient } from '../services';
import { CHEKOUT_SUMMARY_DATA_QUERY_KEY } from '../useCheckoutSummary/types';
import { useQueryClient, useStrictMutation, useStrictQuery } from '../useCustomQuery';
import { GIFT_CARD_QUERY_KEY } from '../useGiftCard/types';
import { SHOPPING_CART_DATA_QUERY_KEY } from '../useShoppingCart/types';

import { COUPON_QUERY_KEY } from './types';

const couponCodeService = new CouponCodeApiService(apiClient);
const couponCodeInteractor = new CouponCodeInteractor(couponCodeService);

const useCouponCode = () => {
  const queryClient = useQueryClient();

  const { data: couponCode } = useStrictQuery(
    [COUPON_QUERY_KEY],
    async () => {
      const res = await couponCodeService.getCouponCode();

      if (res) return res.data;

      return null;
    },
    { retry: false, refetchOnMount: false },
  );

  const {
    data: isCouponApplied,
    error,
    mutateAsync: applyCouponCode,
  } = useStrictMutation(
    async (data: CouponCodeRequest) => {
      const res = await couponCodeInteractor.applyCouponCode(data);
      return res.data;
    },
    {
      onSuccess: () => {
        void queryClient.invalidateQueries([COUPON_QUERY_KEY]);
        void queryClient.invalidateQueries([GIFT_CARD_QUERY_KEY]);
        void queryClient.invalidateQueries([SHOPPING_CART_DATA_QUERY_KEY]);
        void queryClient.invalidateQueries([CHEKOUT_SUMMARY_DATA_QUERY_KEY]);
        return window.scrollTo({ top: 0, behavior: 'smooth' });
      },
    },
  );

  const { mutateAsync: deleteCouponCode } = useStrictMutation(
    async (data: CouponCodeRequest) => {
      await couponCodeInteractor.deleteCouponCode(data);
    },
    {
      onSuccess: () => {
        void queryClient.invalidateQueries([COUPON_QUERY_KEY]);
        void queryClient.invalidateQueries([GIFT_CARD_QUERY_KEY]);
        void queryClient.invalidateQueries([SHOPPING_CART_DATA_QUERY_KEY]);
        return queryClient.invalidateQueries([CHEKOUT_SUMMARY_DATA_QUERY_KEY]);
      },
    },
  );

  return {
    couponCode,
    isCouponApplied,
    error: error as ApiError | undefined,
    couponCodeError: error as ApiError | undefined,
    applyCouponCode,
    deleteCouponCode,
  };
};

export default useCouponCode;
